import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setAlert } from '../../redux/dashboard/actions';
import utils from '../../utils';
import API from '../../services/api';
import Widget from '../../components/widget';
import Loading from '../../components/loading';

const DrawerContent = ({ item, lastUpdated, appliedFilters }) => {
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [currentWidgets, setCurrentWidgets] = useState({});
  const [hasTable, setHasTable] = useState(true);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [dashboardData, setDashboardData] = useState({ rows: [] });

  const dispatch = useDispatch();
  const eventHandlers = useRef([]);

  useEffect(() => {
    return () => {
      eventHandlers.current.forEach(handler => {
        window.removeEventListener('requestResult', handler);
        window.removeEventListener('requestError', handler);
      });
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    API.getTemplates('', "collection = 'DISCOVERY' AND enabled = true")
      .then(response => {
        if (!mounted) return;
        setSelectedDashboard(response?.templates?.[0] || null);
        if (!response?.templates?.length) setLoadingDashboard(false);
      })
      .catch(error => {
        console.error('Error loading Discovery template:', error);
        if (mounted) {
          setLoadingDashboard(false);
          onError(error);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedDashboard) return undefined;

    let isCurrentDashboard = true;
    const widgetReferences = selectedDashboard.rows.flatMap(dashboardRow => dashboardRow.widgets);
    setLoadingDashboard(true);
    setCurrentWidgets({});
    setDashboardData(selectedDashboard);

    Promise.all(
      widgetReferences.map(widgetData =>
        API.getWidget(widgetData.url)
          .then(widget => [widgetData.id, widget])
          .catch(error => {
            onError(error);
            return null;
          })
      )
    ).then(widgets => {
      if (!isCurrentDashboard) return;
      setCurrentWidgets(Object.fromEntries(widgets.filter(Boolean)));
      setLoadingDashboard(false);
    });

    return () => {
      isCurrentDashboard = false;
    };
  }, [selectedDashboard]);

  const onError = e => {
    console.error(e);
    const error = e?.detail && e?.detail?.message ? e?.detail?.message : 'No details';
    let errorMessage = error;
    if (error && error.includes('Not found: Table')) {
      setHasTable(false);
      errorMessage = 'Table not found';
    }

    dispatch(setAlert(utils.generateAlert(errorMessage ? errorMessage : 'Something went wrong.', 'error')));
  };

  const renderEmptyMessage = () => {
    return (
      <div className="no-data-message flex-column">
        <h1>No data found!</h1>
        <p>Nothing to see here. Try choosing another Project or Solution.</p>
      </div>
    );
  };

  const renderWidgets = () => {
    if (!item) {
      return null;
    }

    const widgetReferences = dashboardData.rows.flatMap(dashboardRow => dashboardRow.widgets);
    const visibleWidgets = widgetReferences.filter(widgetData => currentWidgets[widgetData.id]);

    return visibleWidgets.map(widgetData => {
      const queryFilters = {
        startDate: appliedFilters.startDate,
        endDate: appliedFilters.endDate,
        startTime: appliedFilters.startDate,
        endTime: appliedFilters.endDate,
        isTimeRangeLocked: appliedFilters.isTimeRangeLocked,
        filters: appliedFilters.filters || [],
        sites: [item?.siteId],
        datasources: item?.datasources || [],
        timezone: appliedFilters.timezone
      };

      return (
        <div key={`discovery-${widgetData.id}`} className="chart-row">
          <Widget
            widget={currentWidgets[widgetData.id]}
            lastUpdated={lastUpdated}
            filters={queryFilters}
            handleError={onError}
            eventHandlers={eventHandlers.current}
            collection="DISCOVERY"
            customDashboard={selectedDashboard}
          />
        </div>
      );
    });
  };

  return (
    <div className="drawer-content">
      {!hasTable && renderEmptyMessage()}
      {hasTable && (
        <>
          <div className="scrollable-container">
            <div id="discovery-chart-container">
              {loadingDashboard ? (
                <div className="h-100">
                  <Loading size={8} />
                </div>
              ) : (
                renderWidgets()
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DrawerContent;
